'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { aiApi } from '@/lib/api/ai';
import { AiPanelHeader } from './ai-panel-header';
import { AiMessageList } from './ai-message-list';
import { AiChatInput } from './ai-chat-input';
import { AiConversationList } from './ai-conversation-list';
import { toast } from 'sonner';
import { useTranslations } from '@/i18n/useTranslations';
import type { AiMessage } from '@comp-sys/shared';

interface AiChatPanelProps {
  mode: 'fixed' | 'floating';
}

export function AiChatPanel({ mode }: AiChatPanelProps) {
  const t = useTranslations('ai');
  const isOpen = useAiChatStore((s) => s.isOpen);
  const isConversationListOpen = useAiChatStore((s) => s.isConversationListOpen);
  const activeConversationId = useAiChatStore((s) => s.activeConversationId);
  const setMessages = useAiChatStore((s) => s.setMessages);
  const addMessage = useAiChatStore((s) => s.addMessage);
  const setStreaming = useAiChatStore((s) => s.setStreaming);
  const appendStreamingText = useAiChatStore((s) => s.appendStreamingText);
  const setActiveConversation = useAiChatStore((s) => s.setActiveConversation);
  const addApproval = useAiChatStore((s) => s.addApproval);
  const addStreamingToolCall = useAiChatStore((s) => s.addStreamingToolCall);
  const updateStreamingToolCall = useAiChatStore((s) => s.updateStreamingToolCall);
  const panelWidth = useAiChatStore((s) => s.panelWidth);
  const setPanelWidth = useAiChatStore((s) => s.setPanelWidth);
  const autoApprove = useAiChatStore((s) => s.autoApprove);
  const isDragging = useRef(false);
  const toolCallCounter = useRef(0);

  // Load messages when conversation changes
  useEffect(() => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }
    aiApi.getConversation(activeConversationId).then((conv) => {
      if (conv?.messages) {
        setMessages(conv.messages as AiMessage[]);
      }
    }).catch(() => {});
  }, [activeConversationId, setMessages]);

  const handleSend = useCallback(async (content: string) => {
    const userMsg: AiMessage = {
      id: `temp_${Date.now()}`,
      conversationId: activeConversationId || '',
      role: 'user',
      content: [{ type: 'text', text: content }],
      createdAt: new Date().toISOString(),
    };
    addMessage(userMsg);
    setStreaming(true);
    toolCallCounter.current = 0;

    let currentConvId = activeConversationId;
    let accumulatedText = '';

    try {
      const stream = await aiApi.chat(content, activeConversationId || undefined, autoApprove);
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const event = JSON.parse(data);
            switch (event.type) {
              case 'text_delta':
                accumulatedText += event.text as string;
                appendStreamingText(event.text as string);
                break;
              case 'conversation_created':
                currentConvId = event.conversationId as string;
                setActiveConversation(currentConvId);
                break;
              case 'tool_call_start': {
                const tcId = `tc_${toolCallCounter.current++}`;
                addStreamingToolCall({
                  id: tcId,
                  name: event.toolName as string,
                  label: event.label as string || event.toolName as string,
                  status: 'calling',
                  params: event.params as Record<string, unknown> | undefined,
                  description: event.description as string | undefined,
                });
                break;
              }
              case 'tool_result': {
                updateStreamingToolCall(event.toolName as string, {
                  status: 'done',
                  label: (event.label as string) || (event.toolName as string),
                  result: event.result,
                });
                break;
              }
              case 'tool_error': {
                updateStreamingToolCall(event.toolName as string, {
                  status: 'error',
                  label: (event.label as string) || (event.toolName as string),
                  error: event.error as string,
                });
                break;
              }
              case 'approval_request': {
                updateStreamingToolCall(event.toolName as string, {
                  status: 'done',
                  label: (event.label as string) || (event.toolName as string),
                  result: { approvalPending: true },
                });
                addApproval({
                  requestId: event.requestId as string,
                  toolName: event.toolName as string,
                  description: event.description as string,
                  params: event.params as Record<string, unknown>,
                  conversationId: currentConvId || '',
                  messageId: '',
                  userId: '',
                  createdAt: Date.now(),
                });
                break;
              }
              case 'error':
                toast.error(event.error as string);
                break;
            }
          } catch { /* skip */ }
        }
      }

      // After streaming ends, add assistant message to store
      if (accumulatedText) {
        const assistantMsg: AiMessage = {
          id: `ai_${Date.now()}`,
          conversationId: currentConvId || '',
          role: 'assistant',
          content: [{ type: 'text', text: accumulatedText }],
          createdAt: new Date().toISOString(),
        };
        addMessage(assistantMsg);
      }
    } catch {
      toast.error(t('requestFailed'));
    } finally {
      setStreaming(false);
    }
  }, [activeConversationId, autoApprove, addMessage, setStreaming, appendStreamingText, setActiveConversation, addApproval, addStreamingToolCall, updateStreamingToolCall]);

  const handleNewConversation = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
  }, [setActiveConversation, setMessages]);

  // Process SSE stream from approved tool execution (stream comes from approve endpoint)
  const handleApprovedStream = useCallback(async (stream: ReadableStream<Uint8Array>) => {
    setStreaming(true);
    toolCallCounter.current = 0;
    let accumulatedText = '';

    try {
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const event = JSON.parse(data);
            switch (event.type) {
              case 'text_delta':
                accumulatedText += event.text as string;
                appendStreamingText(event.text as string);
                break;
              case 'tool_call_start': {
                const tcId = `tc_${toolCallCounter.current++}`;
                addStreamingToolCall({
                  id: tcId,
                  name: event.toolName as string,
                  label: event.label as string || event.toolName as string,
                  status: 'calling',
                  params: event.params as Record<string, unknown> | undefined,
                  description: event.description as string | undefined,
                });
                break;
              }
              case 'tool_result':
              case 'tool_result_approved': {
                updateStreamingToolCall(event.toolName as string, {
                  status: 'done',
                  label: (event.label as string) || (event.toolName as string),
                  result: event.result,
                });
                break;
              }
              case 'tool_error': {
                updateStreamingToolCall(event.toolName as string, {
                  status: 'error',
                  label: (event.label as string) || (event.toolName as string),
                  error: event.error as string,
                });
                break;
              }
              case 'approval_request': {
                updateStreamingToolCall(event.toolName as string, {
                  status: 'done',
                  label: (event.label as string) || (event.toolName as string),
                  result: { approvalPending: true },
                });
                addApproval({
                  requestId: event.requestId as string,
                  toolName: event.toolName as string,
                  description: event.description as string,
                  params: event.params as Record<string, unknown>,
                  conversationId: activeConversationId || '',
                  messageId: '',
                  userId: '',
                  createdAt: Date.now(),
                });
                break;
              }
              case 'error':
                toast.error(event.error as string);
                break;
            }
          } catch { /* skip */ }
        }
      }

      // After streaming ends, add assistant message to store
      if (accumulatedText) {
        const assistantMsg: AiMessage = {
          id: `ai_${Date.now()}`,
          conversationId: activeConversationId || '',
          role: 'assistant',
          content: [{ type: 'text', text: accumulatedText }],
          createdAt: new Date().toISOString(),
        };
        addMessage(assistantMsg);
      }
    } catch {
      toast.error(t('requestFailed'));
    } finally {
      setStreaming(false);
    }
  }, [activeConversationId, addMessage, setStreaming, appendStreamingText, addApproval, addStreamingToolCall, updateStreamingToolCall]);

  // Drag resize handler
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startX - ev.clientX;
      setPanelWidth(startWidth + delta);
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth, setPanelWidth]);

  if (!isOpen && mode === 'fixed') return null;

  const panel = (
    <div className="relative flex flex-col h-full bg-overlay/70 backdrop-blur-xl border-l border-surface">
      <AiPanelHeader onNewConversation={handleNewConversation} />
      <AiMessageList onApproved={handleApprovedStream} />
      <AiChatInput onSend={handleSend} />
      {isConversationListOpen && <AiConversationList />}
    </div>
  );

  if (mode === 'floating') {
    if (!isOpen) return null;
    return (
      <div className="fixed right-0 top-0 z-50 h-screen shadow-elev animate-in slide-in-from-right duration-200"
        style={{ width: `${panelWidth}px` }}
      >
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-selected-soft active:bg-selected-soft transition-colors z-10"
          onMouseDown={handleMouseDown}
        />
        {panel}
      </div>
    );
  }

  return (
    <div className="shrink-0 sticky top-0 h-screen relative overflow-hidden" style={{ width: `${panelWidth}px` }}>
      <div
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-selected-soft active:bg-selected-soft transition-colors z-10"
        onMouseDown={handleMouseDown}
      />
      {panel}
    </div>
  );
}
